import React, { useState } from 'react';
import { X, Star, Eye, RotateCcw, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContainerImage } from '@libs/models';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface ImagePreviewProps {
  image: ContainerImage | File;
  isUploading?: boolean;
  uploadError?: boolean;
  isMainImage?: boolean;
  displayOrder?: number;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onSetAsMain?: () => void;
  onRemove?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  className?: string;
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  image,
  isUploading = false,
  uploadError = false,
  isMainImage = false,
  displayOrder,
  canMoveUp = false,
  canMoveDown = false,
  onSetAsMain,
  onRemove,
  onMoveUp,
  onMoveDown,
  className,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);

  const getImageUrl = () => {
    if ('url' in image) {
      return image.url;
    } else {
      return URL.createObjectURL(image);
    }
  };

  const getFileName = () => {
    if ('fileName' in image) {
      return image.fileName.split('/').pop() || 'Unknown';
    } else {
      return image.name;
    }
  };

  return (
    <>
      <div
        className={cn(
          'relative group border rounded-lg overflow-hidden bg-white shadow-sm',
          isMainImage && 'ring-2 ring-orange-500 ring-offset-2',
          className,
        )}
      >
        {/* Main Image Badge */}
        {isMainImage && (
          <div className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
            <Star className="h-3 w-3 fill-current" />
            <span>Main</span>
          </div>
        )}

        {/* Display Order Badge */}
        {displayOrder !== undefined && !isMainImage && (
          <div className="absolute top-2 left-2 z-10 bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
            #{displayOrder}
          </div>
        )}

        {/* Image */}
        <div className="aspect-square relative">
          {!imageError ? (
            <img
              src={getImageUrl()}
              alt={getFileName()}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
              <X className="h-8 w-8" />
            </div>
          )}

          {/* Uploading Overlay */}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-white text-sm">
                <RotateCcw className="h-4 w-4 animate-spin" />
                <span>Uploading...</span>
              </div>
            </div>
          )}

          {/* Upload Error Overlay */}
          {uploadError && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-2 text-white text-sm">
                <X className="h-4 w-4" />
                <span>Upload failed</span>
              </div>
            </div>
          )}

          {/* Action Buttons Overlay */}
          {
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex space-x-2">
                {!isUploading && !uploadError && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(true)}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
                {!isUploading && onSetAsMain && !isMainImage && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onSetAsMain();
                    }}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100"
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                )}
                {onRemove && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onRemove();
                    }}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100 hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          }
        </div>

        {/* File Info */}
        <div className="p-2 border-t">
          <p className="text-xs text-gray-600 truncate" title={getFileName()}>
            {getFileName()}
          </p>
          {'size' in image && (
            <p className="text-xs text-gray-400">{(image.size / 1024 / 1024).toFixed(1)} MB</p>
          )}
        </div>

        {/* Order Control Buttons */}
        {(canMoveUp || canMoveDown) && !isUploading && (
          <div className="absolute top-2 right-2 z-10 flex flex-col space-y-1">
            {canMoveUp && onMoveUp && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onMoveUp();
                }}
                className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1 h-6 w-6"
              >
                <MoveUp className="h-3 w-3" />
              </Button>
            )}
            {canMoveDown && onMoveDown && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onMoveDown();
                }}
                className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1 h-6 w-6"
              >
                <MoveDown className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{getFileName()}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={getImageUrl()}
              alt={getFileName()}
              className="max-h-[70vh] max-w-full object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
